import { IHestiaCarePlan, IHestiaCarePlanActivity } from '@hestia/ngx-types';

/**
 * The user types present in DAWBA  questionnaire app.
 * 'Relative' and 'Teacher' are represented by RelatedPerson FHIR resources,
 * whereas 'Patient' is represented by Patient FHIR resources
 * */
export enum DawbaUserType {
  Relative = 'Relative',
  Teacher = 'Teacher',
  Patient = 'Patient',
}

/** Denotes the relationship that the RelatedPerson has to the patient */
export enum DawbaRelationType {
  FamilyOrCareTaker = 'familyOrCareTaker',
  EducationalStaff = 'educationalStaff',
}

export interface IDawbaCarePlanActivity extends IHestiaCarePlanActivity {
  i18nTitle: string;
  i18nInfo: string;
  extension: fhir4.Extension[];
}

export interface IDawbaCarePlan extends IHestiaCarePlan {
  introActivity: IDawbaCarePlanActivity;
  sdqActivity: IDawbaCarePlanActivity;
  finishActivity: IDawbaCarePlanActivity;
  dashboardActivities: Array<IDawbaCarePlanActivity>;
  dashboardActivityCount: number;
  dashboardCompletedActivityCount: number;
  dashboardInProgressActivityCount: number;
}

/* The SINTRO questionnaire should always be present for patients and must be answered before all other questionnaires */
export const SINTRO_QNR_ID = '52e399bd-a585-4855-b13d-663241ade2cd';
/* The PINTRO questionnaire should always be present for patients and must be answered before all other questionnaires */
export const PINTRO_QNR_ID = '43b5d58b-fad8-4d65-9582-4d3d125c3861';
/* The TINTRO questionnaire should always be present for patients and must be answered before all other questionnaires */
export const TINTRO_QNR_ID = '347edf27-a1c3-4f3d-b003-477ac0cc1f80';

/* The SSDQ questionnaire should always be present for patients and must be answered before all other questionnaires */
export const SSDQ_QNR_ID = 'cb31e43f-ba0f-445d-a480-5b3b0b71f010';
/* The PSDQ questionnaire should always be present for patients and must be answered before all other questionnaires */
export const PSDQ_QNR_ID = '827f21d4-f99d-4f21-accd-6da9f72c06c3';
/* The TSDQ questionnaire should always be present for patients and must be answered before all other questionnaires */
export const TSDQ_QNR_ID = 'be0b52a2-3d9a-4bae-ba25-b0636537dc02';

/* The SFINISH questionnaire should always be present for patients and must be answered before all other questionnaires */
export const SFINISH_QNR_ID = 'a1feefb7-588d-44f3-8e1f-e65afb82cddb';
/* The PFINISH questionnaire should always be present for patients and must be answered before all other questionnaires */
export const PFINISH_QNR_ID = 'a49a1fdf-71e4-4e4b-ab55-5410eb10d9b9';
/* The TFINISH questionnaire should always be present for patients and must be answered before all other questionnaires */
export const TFINISH_QNR_ID = 'da5e7080-0536-4858-b70c-e5cd9d0a8ad2';

export interface IDawbaQuestionnaireConfig {
  uuid: string;
  topic: string;
  rgroup: string; // se for children
  routing: boolean;
  openended: boolean;
}

/* Payload for the @setup-careplan-set endpoint that can create patient and related person users alongside their careplans */
export interface ISetupCarePlanSetPayload {
  /* eslint-disable @typescript-eslint/naming-convention */
  dawba_event_uuid: string;
  organization: {
    uuid: string;
    name: string;
  };
  patient: {
    uuid: string;
    cpr: string; // Must be provided, null if unknown
    gender: 'male' | 'female',
    language: string,
    birth_date: string; // %Y-%m-%d
    full_name: string;
    user_name: string;
    password: string;
    due_date: string; // %Y-%m-%d
    available_from_date: string; // %Y-%m-%d
    questionnaires: IDawbaQuestionnaireConfig[];
  };
  related_persons: Array<{
    uuid: string;
    cpr: string; // Must be provided, null if unknown
    user_name: string;
    language: string,
    password: string;
    full_name: string;
    due_date: string;
    available_from_date: string;
    relationship: DawbaRelationType;
    questionnaires: IDawbaQuestionnaireConfig[];
  }>;
  /* eslint-enable @typescript-eslint/naming-convention */
}

export const CHILD_EXAMPLE_QNRS: IDawbaQuestionnaireConfig[] = [
  // These are the default questionnaires for a 14-year old boy.
  {
    uuid: SSDQ_QNR_ID,
    topic: 'ssdq',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '45c9efde-2f78-4ced-a99f-6d965cda95c3',
    topic: 'ssepa',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '984d5e6a-9c7e-43b3-8751-96d63ee317af',
    topic: 'sspph',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '7d6f4da7-7f2a-414c-bf06-51ac4d77e81d',
    topic: 'ssoph',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'fa9d25bb-d4f2-4738-97e6-c9e6bc7a090e',
    topic: 'span',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'f1c4993b-05b9-4b57-8d4b-da3c5e87b5cd',
    topic: 'sptsd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'bb266315-e1fe-4781-821c-e9310bca9fe6',
    topic: 'socd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '4c8ad662-61c6-42bf-80dc-9f90c951f59b',
    topic: 'sbdd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '783e4fb2-fa83-484e-9a8d-814ac7024c7a',
    topic: 'sgena',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'bc258220-c1ff-4255-807b-812635425fe8',
    topic: 'sdep',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '95b8a6b9-0d7a-47fe-a79b-d6ce7bc80fe9',
    topic: 'sdmdd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '4f85f972-236c-4e79-b691-d4f75111db77',
    topic: 'sbip',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'b65393f1-9909-4ccb-a791-23ad96a92d03',
    topic: 'sadhd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '8effa696-ff0b-45d2-aa06-9955151f74af',
    topic: 'sodcd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '84741a29-02db-457c-acec-581afac9526f',
    topic: 'ssub',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'fe6e1c41-d951-4664-a96a-22ab7192c1da',
    topic: 'seat',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'cd71bf3d-75f9-4634-baf3-03483e327bfc',
    topic: 'spsy',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '2cb7777c-7e52-45e6-8a4b-881b5785a5fb',
    topic: 'spos',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
];

export const PARENT_EXAMPLE_QNRS: IDawbaQuestionnaireConfig[] = [
  // These are the default questionnaires for a 14-year old boy, only change is psdq instead of ssdq.
  {
    uuid: PSDQ_QNR_ID,
    topic: 'psdq',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '45c9efde-2f78-4ced-a99f-6d965cda95c3',
    topic: 'ssepa',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '984d5e6a-9c7e-43b3-8751-96d63ee317af',
    topic: 'sspph',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '7d6f4da7-7f2a-414c-bf06-51ac4d77e81d',
    topic: 'ssoph',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'fa9d25bb-d4f2-4738-97e6-c9e6bc7a090e',
    topic: 'span',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'f1c4993b-05b9-4b57-8d4b-da3c5e87b5cd',
    topic: 'sptsd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'bb266315-e1fe-4781-821c-e9310bca9fe6',
    topic: 'socd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '4c8ad662-61c6-42bf-80dc-9f90c951f59b',
    topic: 'sbdd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '783e4fb2-fa83-484e-9a8d-814ac7024c7a',
    topic: 'sgena',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'bc258220-c1ff-4255-807b-812635425fe8',
    topic: 'sdep',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '95b8a6b9-0d7a-47fe-a79b-d6ce7bc80fe9',
    topic: 'sdmdd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '4f85f972-236c-4e79-b691-d4f75111db77',
    topic: 'sbip',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'b65393f1-9909-4ccb-a791-23ad96a92d03',
    topic: 'sadhd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '8effa696-ff0b-45d2-aa06-9955151f74af',
    topic: 'sodcd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '84741a29-02db-457c-acec-581afac9526f',
    topic: 'ssub',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'fe6e1c41-d951-4664-a96a-22ab7192c1da',
    topic: 'seat',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'cd71bf3d-75f9-4634-baf3-03483e327bfc',
    topic: 'spsy',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '2cb7777c-7e52-45e6-8a4b-881b5785a5fb',
    topic: 'spos',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
];

export const TEACHER_EXAMPLE_QNRS: IDawbaQuestionnaireConfig[] = [
  // These are the default questionnaires for a 14-year old boy, only change is tsdq instead of ssdq.
  {
    uuid: TSDQ_QNR_ID,
    topic: 'tsdq',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '45c9efde-2f78-4ced-a99f-6d965cda95c3',
    topic: 'ssepa',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '984d5e6a-9c7e-43b3-8751-96d63ee317af',
    topic: 'sspph',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '7d6f4da7-7f2a-414c-bf06-51ac4d77e81d',
    topic: 'ssoph',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'fa9d25bb-d4f2-4738-97e6-c9e6bc7a090e',
    topic: 'span',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'f1c4993b-05b9-4b57-8d4b-da3c5e87b5cd',
    topic: 'sptsd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'bb266315-e1fe-4781-821c-e9310bca9fe6',
    topic: 'socd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '4c8ad662-61c6-42bf-80dc-9f90c951f59b',
    topic: 'sbdd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '783e4fb2-fa83-484e-9a8d-814ac7024c7a',
    topic: 'sgena',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'bc258220-c1ff-4255-807b-812635425fe8',
    topic: 'sdep',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '95b8a6b9-0d7a-47fe-a79b-d6ce7bc80fe9',
    topic: 'sdmdd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '4f85f972-236c-4e79-b691-d4f75111db77',
    topic: 'sbip',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'b65393f1-9909-4ccb-a791-23ad96a92d03',
    topic: 'sadhd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '8effa696-ff0b-45d2-aa06-9955151f74af',
    topic: 'sodcd',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '84741a29-02db-457c-acec-581afac9526f',
    topic: 'ssub',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'fe6e1c41-d951-4664-a96a-22ab7192c1da',
    topic: 'seat',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: 'cd71bf3d-75f9-4634-baf3-03483e327bfc',
    topic: 'spsy',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
  {
    uuid: '2cb7777c-7e52-45e6-8a4b-881b5785a5fb',
    topic: 'spos',
    rgroup: 'se',
    routing: true,
    openended: true,
  },
];
